import { initializeApp } from "firebase/app";
import { initializeAuth, indexedDBLocalPersistence, browserLocalPersistence } from "firebase/auth";
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  memoryLocalCache,
} from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getMessaging, getToken, onMessage } from "firebase/messaging";

const firebaseConfig = {
  // TODO: Replace with your Firebase config from the Firebase Console
  // Go to: Firebase Console → Project Settings → General → Your apps → Web app
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: "home-performance-d52d9.firebaseapp.com",
  projectId: "home-performance-d52d9",
  storageBucket: "home-performance-d52d9.firebasestorage.app",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);

export const auth = initializeAuth(app, {
  persistence: [indexedDBLocalPersistence, browserLocalPersistence],
});

// Firestore with IndexedDB-backed offline persistence.
// Multi-tab manager so concurrent tabs share the same cached state.
// Falls back to in-memory cache if IndexedDB is unavailable (private mode, unsupported browsers).
export const db = (() => {
  try {
    return initializeFirestore(app, {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
      }),
    });
  } catch (err) {
    console.warn("Firestore persistent cache unavailable, using memory cache:", err);
    return initializeFirestore(app, { localCache: memoryLocalCache() });
  }
})();

export const storage = getStorage(app);
export const functions = getFunctions(app, "us-central1");

// Callable Cloud Functions
export const translateText = httpsCallable(functions, "translate");
export const transcribeAudio = httpsCallable(functions, "transcribe");

// ── Firebase Cloud Messaging ───────────────────────────────────
let messaging = null;
try {
  messaging = getMessaging(app);
} catch (err) {
  // FCM not supported (e.g. Safari private browsing, older browsers)
  console.warn("FCM not supported in this browser:", err);
}

export { messaging };

/**
 * Request notification permission, get FCM token, and return it.
 * Returns null if permission denied or FCM not supported.
 */
export async function requestNotificationToken() {
  if (!messaging) return null;

  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      console.log("Notification permission denied.");
      return null;
    }

    // VAPID key — needed for web push
    // CC will need to generate this via: firebase console → Project Settings → Cloud Messaging → Web Push certificates
    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
      console.warn("No VAPID key set. Add REACT_APP_FIREBASE_VAPID_KEY to .env");
      return null;
    }

    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: await navigator.serviceWorker.getRegistration(),
    });

    return token;
  } catch (err) {
    console.error("Error getting FCM token:", err);
    return null;
  }
}

/**
 * Listen for foreground messages (when app is open).
 */
export function onForegroundMessage(callback) {
  if (!messaging) return () => {};
  return onMessage(messaging, callback);
}

export default app;
